import type { CSSProperties } from 'react';

/**
 * Parent form state updater type. The parent's full state shape varies between callers,
 * but the callback always uses spread ({ ...prev, field: value }) to preserve all fields.
 * TypeScript's SetStateAction is invariant, so the parent type cannot be expressed here
 * without a generic component.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- parent state type varies; see comment above
type ParentFormUpdater = (fn: (prev: any) => any) => void;

interface CustomerFormFinancialSectionProps {
  expanded: boolean;
  onToggle: () => void;
  formData: {
    payment_terms?: number;
    credit_limit?: number | string;
    vat_reg_no?: string;
    vat_treatment?: string;
    is_taxable?: boolean;
    customer_type?: string;
  };
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  setFormData: ParentFormUpdater;
  setDisplayNameManual: (val: boolean) => void;
  styles: Record<string, CSSProperties>;
}

export default function CustomerFormFinancialSection({
  expanded,
  onToggle,
  formData,
  onChange,
  setFormData,
  setDisplayNameManual,
  styles,
}: CustomerFormFinancialSectionProps) {
  return (
    <div style={styles.section}>
      <div
        style={styles.collapsibleHeader}
        onClick={onToggle}
      >
        <span style={styles.sectionTitle}>
          {expanded ? '▼' : '▶'} Financial & Tax
        </span>
        <span style={styles.expandHint}>
          {expanded ? 'Collapse' : 'Expand'}
        </span>
      </div>

      {expanded && (
        <div style={styles.collapsibleContent}>
          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Payment Terms</label>
              <select
                name="payment_terms"
                style={styles.input}
                value={formData.payment_terms}
                onChange={onChange}
              >
                <option value={0}>Due on Receipt</option>
                <option value={7}>Net 7</option>
                <option value={14}>Net 14</option>
                <option value={30}>Net 30</option>
                <option value={45}>Net 45</option>
                <option value={60}>Net 60</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Credit Limit</label>
              <input
                type="number"
                name="credit_limit"
                style={styles.input}
                value={formData.credit_limit}
                onChange={onChange}
                placeholder="0.00"
                step="0.01"
              />
            </div>
          </div>

          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>VAT Number</label>
              <input
                type="text"
                name="vat_reg_no"
                style={styles.input}
                value={formData.vat_reg_no}
                onChange={onChange}
                placeholder="Enter VAT number"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>VAT Treatment</label>
              <select
                name="vat_treatment"
                style={styles.input}
                value={formData.vat_treatment}
                onChange={onChange}
              >
                <option value="">Select...</option>
                <option value="vat_registered">VAT Registered</option>
                <option value="vat_not_registered">Not VAT Registered</option>
                <option value="eu_vat_registered">EU VAT Registered</option>
                <option value="overseas">Overseas (Zero-rated)</option>
              </select>
            </div>
          </div>

          {/* Warning: VAT number entered but customer type is B2C */}
          {formData.vat_reg_no && formData.customer_type === 'individual' && (
            <div
              className="flex items-start gap-sm mb-md rounded"
              style={{
                background: 'var(--color-warning-bg)',
                border: '1px solid #f59e0b',
                padding: '0.75rem 1rem',
              }}
            >
              <span style={{ fontSize: 'var(--text-2xl)', lineHeight: 1 }}>&#9888;</span>
              <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-warning-text)' }}>
                <strong>Customer type mismatch:</strong> A VAT number has been entered but this customer is set to Individual (B2C). B2C customers cannot be VAT registered and the VAT number will not appear on invoices.{' '}
                <button
                  type="button"
                  className="fw-600 cursor-pointer p-0 border-none"
                  style={{ background: 'none', color: 'var(--color-primary-light)', fontSize: 'var(--text-md)', textDecoration: 'underline' }}
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      customer_type: 'business',
                      vat_treatment: prev.vat_treatment || 'vat_registered',
                    }));
                    setDisplayNameManual(false);
                  }}
                >
                  Switch to Business (B2B)
                </button>
              </div>
            </div>
          )}

          <div style={styles.checkboxRow}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="is_taxable"
                checked={formData.is_taxable}
                onChange={onChange}
              />
              <span>Customer is taxable</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
